import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useLocation,
  Navigate
} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as Yup from 'yup';
import { Formik } from 'formik';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
// import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import CircularIndeterminate from 'Components/loader/CircularIndeterminate';
import { MicrosoftSignInButton } from 'Components/buttons/login/MicrosoftSignInButton';
import HomeRedirect from 'Components/empty/Redirect';
// import { GoogleSignInButton } from "../components/buttons/login/GoogleSignInButton";
import { useAuth } from 'Hooks/useAuth';
import Stack from '@mui/material/Stack';
import { useIsAuthenticated } from '@azure/msal-react';

const LoginContainer = styled(Container)(({ theme }) => ({
  maxWidth: '300px',
  marginTop: '150px',
  justifyContent: 'center'
}));

const EmailLoginBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.background,
  display: 'flex',
  flexDirection: 'column',
  // width: "150px",
  // maxWidth: "150px",
  // height: "100%",
  justifyContent: 'center',
  padding: `${theme.spacing(4)} ${theme.spacing(4)}`,
  borderRadius: `${theme.spacing(1)}`
}));

const SocialLoginBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  justifyContent: 'center',
  marginTop: `${theme.spacing(2)}`,
  padding: `${theme.spacing(1)} ${theme.spacing(1)}`,
  borderRadius: `${theme.spacing(1)}`
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  fontWeight: `bold`
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.primary.background,
  color: theme.palette.primary.contrastText,
  fontWeight: `bold`
}));

const StyledButton = styled(Button)(({ theme }) => {
  // console.log(theme, "this is theme object")
  return {
    backgroundColor: theme.palette.secondary.main,
    '&: active': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.85)
    },
    color: theme.palette.primary.contrastText,
    '&: svg': {
      marginRight: `${theme.spacing(1)}`
    }
  };
});

const Login = () => {
  const navigate = useNavigate();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [minimumTime, setMinimumTime] = useState(2000);
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState(true);
  const isUserAuthenticated = useIsAuthenticated();
  // const { isAuthenticated, login } = useAuth()

  const restartTimeout = useCallback(() => {
    // let controller = new AbortController();
    setMinimumTimeElapsed(false);
    setTimeout(() => {
      setMinimumTimeElapsed(true);
      // controller = null;
    }, minimumTime);
    // return () => controller?.abort();
  }, [setMinimumTimeElapsed, minimumTime]);

  useEffect(() => {
    let controller = new AbortController();
    if (isUserAuthenticated) {
      restartTimeout();
      // login()
      controller = null;
    }
    return () => controller?.abort();
  }, []);

  function handleOnClick() {
    localStorage.setItem('signin_type', 'email');
  }

  const initialValues = {
    email: '',
    password: ''
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Must be a valid email')
      .max(255)
      .required('Email is required'),
    password: Yup.string().max(255).required('Password is required')
  });

  const onSubmit = (values) => {
    localStorage.setItem('email', values.email);
    localStorage.setItem('password', values.password);
    localStorage.setItem('signing_in', true);
    setIsSigningIn(true);
    setTimeout(() => {
      navigate('/app/auth', { replace: true });
    }, 2000);
  };

  return (
    <>
      <Helmet>
        <title>Login</title>
      </Helmet>
      {!minimumTimeElapsed ? (
        <HomeRedirect />
      ) : (
        // <PageLoadingSkeleton variant={"rectangular"} marginx={0} marginy={0} count={1} height={500} />
        <LoginContainer>
          <EmailLoginBox>
            <Stack spacing={2}>
              <Formik
                // enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values
                }) => {
                  return (
                    // <EmailLoginForm>
                    <Stack spacing={2}>
                      <form onSubmit={handleSubmit}>
                        <StyledTypography align='center' variant='body1'>
                          Login with email address
                        </StyledTypography>

                        <StyledTextField
                          error={Boolean(touched.email && errors.email)}
                          fullWidth
                          helperText={touched.email && errors.email}
                          label='Email Address'
                          margin='normal'
                          name='email'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type='email'
                          value={values.email}
                          variant='outlined'
                        />
                        <StyledTextField
                          error={Boolean(touched.password && errors.password)}
                          fullWidth
                          helperText={touched.password && errors.password}
                          label='Password'
                          margin='normal'
                          name='password'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type='password'
                          value={values.password}
                          variant='outlined'
                        />
                        <Box sx={{ py: 2 }}>
                          <StyledButton
                            fullWidth
                            size='large'
                            type='submit'
                            variant='contained'
                            onClick={() => handleOnClick()}
                          >
                            {isSigningIn ? (
                              <>
                                Signing In... <CircularIndeterminate />
                              </>
                            ) : (
                              'Sign in now'
                            )}
                          </StyledButton>
                        </Box>
                      </form>
                    </Stack>
                    // </EmailLoginForm>
                  );
                }}
              </Formik>

              <SocialLoginBox
                display='flex'
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                alignContent='center'
                wrap='nowrap'
              >
                <Stack direction='row' spacing={2}>
                  <MicrosoftSignInButton />
                  {/* <GoogleSignInButton /> */}
                </Stack>
              </SocialLoginBox>
            </Stack>
          </EmailLoginBox>
        </LoginContainer>
      )}
    </>
  );
  // }
};

export default Login;
